import type { ArenaGameplayDefinition } from './types';

/**
 * Validates the geometry assumptions used by the deterministic ground router.
 * This intentionally stays independent from content files and the engine so it
 * can be used both at content-authoring time and in tests.
 */
export type NavigationGeometryInput = Pick<ArenaGameplayDefinition, 'worldBounds' | 'riverTop' | 'riverBottom' | 'bridges'> & {
  towerSockets: Readonly<Record<string, { x: number; y: number }>>;
};

export function validateNavigationGeometry(gameplay: NavigationGeometryInput, prefix = 'arena'): string[] {
  const errors: string[] = [];
  const { worldBounds, riverTop, riverBottom, bridges } = gameplay;
  const right = worldBounds.x + worldBounds.width;

  if (riverTop >= riverBottom) errors.push(`${prefix}:river-order`);
  if (riverTop < worldBounds.y || riverBottom > worldBounds.y + worldBounds.height) errors.push(`${prefix}:river-out-of-bounds`);
  if (bridges.length < 2) errors.push(`${prefix}:requires-two-bridges`);
  if (new Set(bridges.map((bridge) => bridge.id)).size !== bridges.length) errors.push(`${prefix}:duplicate-bridge-id`);

  for (const bridge of bridges) {
    const left = bridge.x - bridge.width / 2;
    const bridgeRight = bridge.x + bridge.width / 2;
    if (bridge.width <= 0) errors.push(`${prefix}:bridge-non-positive-width:${bridge.id}`);
    if (left < worldBounds.x || bridgeRight > right) errors.push(`${prefix}:bridge-out-of-bounds:${bridge.id}`);
    if (bridge.x < worldBounds.x || bridge.x > right) errors.push(`${prefix}:bridge-center-out-of-bounds:${bridge.id}`);
  }

  const hasTopSocket = Object.entries(gameplay.towerSockets).some(([id, point]) => id.startsWith('bot') && point.y < riverTop);
  const hasBottomSocket = Object.entries(gameplay.towerSockets).some(([id, point]) => id.startsWith('player') && point.y > riverBottom);
  if (!hasTopSocket) errors.push(`${prefix}:missing-top-route-socket`);
  if (!hasBottomSocket) errors.push(`${prefix}:missing-bottom-route-socket`);
  if (!bridges.some((bridge) => bridge.x >= worldBounds.x && bridge.x <= right)) errors.push(`${prefix}:no-usable-bridge`);

  return errors;
}
